/**
 * AudienceIntake — plays, visits, programme reminders and partnership
 * enquiries coming off the public Faith Works TV pages.
 */
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import api from '@/lib/api';

const Tile = ({ label, value, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
      <p className="mt-3 text-3xl font-bold tabular-nums">{value ?? 0}</p>
    </CardContent>
  </Card>
);

export const AudienceIntake = () => {
  const qc = useQueryClient();
  const { data, isFetching } = useQuery({
    queryKey: ['fwtv-intake'],
    queryFn: () => api.get('/fwtv/studio/intake').then((r) => r.data),
  });

  return (
    <div className="space-y-5" data-testid="fwtv-intake">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-slate-600">
          Last {data?.window_days || 30} days of viewer response.
        </p>
        <Button variant="outline" size="sm" data-testid="intake-refresh"
                onClick={() => qc.invalidateQueries({ queryKey: ['fwtv-intake'] })}>
          <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile label="Recorded plays" value={data?.plays} testId="intake-plays" />
        <Tile label="Page visits" value={data?.page_visits} testId="intake-visits" />
        <Tile label="Programme reminders" value={data?.reminders_total} testId="intake-reminders" />
        <Tile label="Partnership enquiries" value={data?.enquiries_total} testId="intake-enquiries" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
            Recent partnership enquiries
          </h3>
          <div className="mt-3 border rounded-md divide-y" data-testid="intake-enquiry-list">
            {(data?.recent_enquiries || []).length === 0 ? (
              <p className="p-4 text-sm text-slate-500">Nothing yet.</p>
            ) : data.recent_enquiries.map((e) => (
              <div key={e.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <span className="flex-1 min-w-0 truncate">{e.name}</span>
                <span className="tabular-nums">{e.amount ? `$${e.amount}` : '—'}</span>
                <span className="text-xs text-slate-500">
                  {new Date(e.created_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
            Recent reminders
          </h3>
          <div className="mt-3 border rounded-md divide-y" data-testid="intake-reminder-list">
            {(data?.recent_reminders || []).length === 0 ? (
              <p className="p-4 text-sm text-slate-500">Nothing yet.</p>
            ) : data.recent_reminders.map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <span className="flex-1 min-w-0 truncate">{r.programme}</span>
                <span className="text-xs uppercase tracking-wide text-slate-500">{r.delivery}</span>
                <span className="text-xs text-slate-500">
                  {new Date(r.created_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudienceIntake;
